import { useState } from 'react'
import { useAtom } from 'jotai'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import AddRoundedIcon from '@mui/icons-material/AddRounded'

import RichText from '@/components/rich-text'
import { selectedMunicipalityIdAtom } from "@/state"
import { useCollectionCalendarInfos } from '@/hooks/api/use-collection-calendar-info'
import LoadingBox from "@/components/loading-box"
import ErrorBox from "@/components/error-box"
import { Endpoints, UserRole } from '@/types'
import DocumentsList from '@/components/attachments/documents-list'
import PermissionsGate from '@/components/permissions-gate'
import CollectionCalendarInfoForm from '../forms/collection-calendar-info'
import CollectionCalendarInfoActions from './actions'

function CollectionCalendarInfo() {

    const [municipalityId] = useAtom(selectedMunicipalityIdAtom)

    const [create, setCreate] = useState<boolean>(false)
    const [edit, setEdit] = useState<boolean>(false)

    const { status, data, error } = useCollectionCalendarInfos(municipalityId)

    if (status === 'pending') {
        return (
            <LoadingBox />
        )
    }

    if (error) {
        return <ErrorBox
            error={error}
            message={`Nepovedlo se načíst obsah sekce -Svozový kalendář- obce s ID ${municipalityId}`}
        />
    }

    if (data.length === 0) {
        return (
            <Box pt={2}>
                {create ?
                    <CollectionCalendarInfoForm
                        closeCallback={() => setCreate(false)}
                    />
                    :
                    <Box>
                        <PermissionsGate
                            userRoles={[
                                UserRole.basic,
                                UserRole.collectionCompany,
                                UserRole.collectionPointAdmin,
                                UserRole.eokoDataAdmin,
                                UserRole.resident,
                            ]}
                        >
                            <Typography variant="body1" component="p" mb={2}>
                                Tato sekce je zatím prázdná
                            </Typography>
                        </PermissionsGate>
                        <PermissionsGate
                            userRoles={[
                                UserRole.admin,
                                UserRole.municipalityOfficial,
                            ]}
                        >
                            <Typography variant="body1" component="p" mb={2}>
                                Tuto sekci jste zatím nevytvořili. Přejete si ji vytvořit?
                            </Typography>
                            <Button
                                variant="contained"
                                color="terciary"
                                startIcon={<AddRoundedIcon />}
                                onClick={() => setCreate(true)}
                            >
                                Vytvořit sekci
                            </Button>
                        </PermissionsGate>
                    </Box>
                }
            </Box>
        )
    }

    const collectionCalendarInfo = data[0]

    return (
        <Box pt={2}>
            {edit ?
                <CollectionCalendarInfoForm
                    collectionCalendarInfo={collectionCalendarInfo}
                    closeCallback={() => setEdit(false)}
                />
                :
                <>
                    {collectionCalendarInfo.description &&
                        <RichText content={collectionCalendarInfo.description} />
                    }
                    <PermissionsGate
                        userRoles={[
                            UserRole.admin,
                            UserRole.municipalityOfficial,
                        ]}
                    >
                        <Box sx={{
                            position: 'absolute',
                            top: -8,
                            right: -8,
                            backgroundColor: 'rgba(255, 255, 255, 0.8)',
                            borderRadius: '1rem',
                        }}>
                            <CollectionCalendarInfoActions
                                collectionCalendarInfo={collectionCalendarInfo}
                                setEdit={setEdit}
                            />
                        </Box>
                    </PermissionsGate>
                </>
            }
            <Box pt={4} />
            <DocumentsList
                documents={collectionCalendarInfo.documents}
                endpoint={Endpoints.collectionCalendarInfo}
                entityId={collectionCalendarInfo.id}
                invalidateQueryKey={['collectionCalendarInfos', municipalityId]}
            />
        </Box>
    )
}

export default CollectionCalendarInfo
